import { PostHogProvider } from '@posthog/react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import type { PropsWithChildren } from 'react';

import { ENVIRONMENT } from '@/constants';

import { eventSchemas, type AnalyticsEventName, type AnalyticsEventProperties } from './event-schemas';
import { normalizePostHogPageEvent } from './posthog-page-event-normalizer';

// This module is the ONLY place allowed to touch the PostHog SDK (enforced by ESLint). Everything
// else emits through capture()/identify()/reset().

const projectToken = ENVIRONMENT.VITE_POSTHOG_PROJECT_TOKEN;
if (projectToken) {
  posthog.init(projectToken, {
    api_host: ENVIRONMENT.VITE_POSTHOG_HOST,
    // Pin PostHog's date-gated SDK defaults to the wizard-generated version.
    defaults: '2026-01-30',
    capture_performance: { web_vitals: true },
    before_send: normalizePostHogPageEvent,
  });
}

export const AnalyticsProvider = ({ children }: PropsWithChildren) => (
  <PostHogProvider client={posthog}>{children}</PostHogProvider>
);

// The properties arg is optional only for events whose fields are all optional (e.g. no payload).
type CaptureArgs<K extends AnalyticsEventName> = Record<string, never> extends AnalyticsEventProperties<K>
  ? [properties?: AnalyticsEventProperties<K>]
  : [properties: AnalyticsEventProperties<K>];

// Emit an analytics event. The payload is validated against the event's schema and any field not
// declared there is dropped before it reaches PostHog, so no undeclared data (incl. PHI) can leak.
export function capture<K extends AnalyticsEventName>(name: K, ...[properties]: CaptureArgs<K>): void {
  const result = eventSchemas[name].safeParse(properties ?? {});
  if (!result.success) {
    if (ENVIRONMENT.VITE_ENVIRONMENT === 'dev') {
      console.error(`[analytics] dropped "${name}" — invalid payload`, result.error.flatten());
    }
    return;
  }
  posthog.capture(name, result.data);
}

export function identify(userId: string, traits?: Record<string, unknown>): void {
  posthog.identify(userId, traits);
  Sentry.setUser({ id: userId });
}

export function reset(): void {
  posthog.reset();
  Sentry.setUser(null);
}
